import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, errorCode, errorDetails, errorMessage } from '../lib/api';
import { formatDuration } from '../lib/tests';
import { partOfQuestion, type Sitting } from '../lib/sitting';
import { useSitting } from '../exam/useSitting';
import { unlockExamAudio } from '../exam/exam-audio';
import { ExamGroup } from '../exam/ExamGroup';
import { DragProvider } from '../exam/drag';
import { Markable } from '../exam/Markable';
import { BreachWarning } from '../exam/BreachWarning';
import { forgetMarks } from '../exam/mark-store';
import { PassagePane, type PassageSlot } from '../exam/PassagePane';
import { headingSlots, passageHasParagraphs } from '../exam/headings';
import { writingPromptHtml } from '../exam/writing';
import { ExamNav, ExamTopBar, DEFAULT_SETTINGS, type Settings } from '../exam/ExamChrome';
import { ListeningAudio, type ListeningHandle } from '../exam/ListeningAudio';
import { enterExamFullscreen, useLockdown } from '../exam/useLockdown';

type Phase = 'ready' | 'sitting' | 'finished';

/**
 * How long the opening screen stays, at the least.
 *
 * The real screen holds one while it prepares the paper, and a candidate who
 * has just pressed the button reads it as the exam taking over. Ours is
 * usually ready in a few hundred milliseconds; without a floor the screen
 * would flash rather than land.
 */
const OPENING_MS = 1_600;

/** The screen between pressing the button and the paper, as the exam has it. */
function ExamOpening() {
  return (
    <div className="exam exam--opening" data-scheme="bw" data-size="regular">
      <div className="opening">
        <svg className="opening__spinner" viewBox="0 0 50 50" aria-hidden="true" focusable="false">
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeDasharray="94 32"
          />
        </svg>
        <h1 className="opening__title">Your test will begin shortly</h1>
        <p className="opening__note">Please wait</p>
      </div>
    </div>
  );
}

export function ExamPage() {
  const { assignmentId = '' } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('ready');
  const [sittingId, setSittingId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [opening, setOpening] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  /**
   * A refusal a candidate cannot act on from inside the page: the centre
   * requires Safe Exam Browser and this is not it. Shown on its own rather
   * than as a line of red text beside a button they can press again.
   */
  const [needsSeb, setNeedsSeb] = useState<'NO_HEADER' | 'NO_MATCH' | null>(null);

  const start = useCallback(async () => {
    setStarting(true);
    setOpening(true);
    setStartError(null);
    // Asked for inside the click, which is the only moment a browser grants
    // it. Failure is not fatal: the player covers the screen either way and
    // says so when it is not in front.
    enterExamFullscreen();
    const pressedAt = Date.now();
    try {
      const { data } = await api.post<Sitting>(`/my/assignments/${assignmentId}/start`);
      // The clock is already running on the server by now, so the wait is kept
      // short — long enough to read, not long enough to cost anyone an answer.
      const left = OPENING_MS - (Date.now() - pressedAt);
      if (left > 0) await new Promise((resolve) => setTimeout(resolve, left));
      setSittingId(data.session.id);
      setPhase(data.session.state === 'IN_PROGRESS' ? 'sitting' : 'finished');
      setOpening(false);
    } catch (err) {
      if (errorCode(err) === 'SEB_REQUIRED') {
        setNeedsSeb(errorDetails<{ reason: 'NO_HEADER' | 'NO_MATCH' }>(err)?.reason ?? 'NO_HEADER');
      } else {
        setStartError(errorMessage(err, 'This paper could not be opened'));
      }
      setOpening(false);
    } finally {
      setStarting(false);
    }
  }, [assignmentId]);

  if (opening) return <ExamOpening />;

  /**
   * The centre requires Safe Exam Browser and this is not it.
   *
   * Its own screen rather than an error beside the start button: there is
   * nothing to try again, and a candidate who does not know what SEB is needs
   * to be told to go and ask, not to press the button harder.
   */
  if (needsSeb) {
    return <NeedsSafeExamBrowser reason={needsSeb} onLeave={() => navigate('/my/tests')} />;
  }

  if (phase === 'ready') {
    return (
      <ReadyScreen
        assignmentId={assignmentId}
        starting={starting}
        error={startError}
        onStart={start}
        onLeave={() => navigate('/my/tests')}
      />
    );
  }

  if (!sittingId) return null;
  return <Sit sittingId={sittingId} onLeave={() => navigate('/my/tests')} />;
}

/**
 * The screen before the clock starts.
 *
 * For Listening it includes a sound check, as the real test does. That is not
 * only fidelity: a browser refuses to play sound until the person has
 * interacted with the page, so without a deliberate check the recording could
 * be silently blocked while the exam clock — which the server owns — ran on
 * regardless.
 */
/**
 * The centre requires Safe Exam Browser and this is not it.
 *
 * Its own screen rather than an error beside a button: there is nothing to try
 * again, and a candidate who has never heard of SEB needs to be told to go and
 * ask for it, not to press the button harder. Shown both to somebody starting
 * a paper and to somebody resuming one in the wrong browser.
 */
function NeedsSafeExamBrowser({
  reason,
  onLeave,
}: {
  reason: 'NO_HEADER' | 'NO_MATCH';
  onLeave: () => void;
}) {
  return (
    <div className="exam exam--opening" data-scheme="bw" data-size="regular">
      <div className="opening" style={{ maxWidth: '40rem' }}>
        <h1 className="opening__title">This test must be taken in Safe Exam Browser</h1>
        {/*
          * The heading has already said what is wrong, so this says what to do
          * about it — and says something different when Safe Exam Browser IS
          * running, because "open it in SEB" is no help to somebody who has.
          */}
        <p className="opening__note" style={{ fontWeight: 400, marginTop: '1rem' }}>
          {reason === 'NO_MATCH'
            ? 'Safe Exam Browser is running a different configuration from the one this centre expects.'
            : 'Your centre will give you a file that opens it.'}
        </p>
        <p className="opening__note" style={{ fontWeight: 400, opacity: 0.75 }}>
          Ask your centre, and nothing is lost — your paper is where you left it and the clock is
          not running.
        </p>
        <button className="xbtn" style={{ marginTop: '1.5rem' }} onClick={onLeave}>
          Back to my tests
        </button>
      </div>
    </div>
  );
}

function ReadyScreen({
  assignmentId,
  starting,
  error,
  onStart,
  onLeave,
}: {
  assignmentId: string;
  starting: boolean;
  error: string | null;
  onStart: () => void;
  onLeave: () => void;
}) {
  const [info, setInfo] = useState<{
    title: string;
    module: string;
    durationMs: number | null;
    resuming: boolean;
  } | null>(null);
  const [soundChecked, setSoundChecked] = useState(false);
  const [checking, setChecking] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);
  const probe = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    api
      .get<{ assignments: { id: string; test: { title: string; module: string; durationMs: number | null }; session: { state: string } | null }[] }>(
        '/my/assignments',
      )
      .then(({ data }) => {
        const row = data.assignments.find((a) => a.id === assignmentId);
        if (row) {
          setInfo({
            title: row.test.title,
            module: row.test.module,
            durationMs: row.test.durationMs,
            resuming: row.session?.state === 'IN_PROGRESS',
          });
        }
      })
      .catch((err) =>
        // Without this the screen sits on "Loading…" with a button that
        // cannot be pressed and says nothing about why.
        setInfoError(errorMessage(err, 'This paper could not be loaded. Check your connection.')),
      );
  }, [assignmentId]);


  const isListening = info?.module === 'LISTENING';
  const isWritingPaper = info?.module === 'WRITING';

  const moduleName =
    info?.module === 'READING' ? 'Reading' : isWritingPaper ? 'Writing' : 'Listening';

  async function soundCheck() {
    // Inside the click: leaves the recording's player allowed to play.
    if (isListening) unlockExamAudio();
    setChecking(true);
    try {
      // A short tone generated in the page: it proves the speakers work and
      // gives the browser the interaction it needs, without spending any of
      // the real recording.
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      await new Promise((r) => setTimeout(r, 800));
      osc.stop();
      await ctx.close();
      setSoundChecked(true);
    } catch {
      // If the tone cannot play the candidate should still be able to proceed;
      // the player has its own fallback prompt.
      setSoundChecked(true);
    } finally {
      setChecking(false);
      probe.current = null;
    }
  }

  return (
    <div className="exam" data-scheme="bw" data-size="regular">
      <div />
      <div className="exam__scroll" style={{ display: 'grid', placeItems: 'center' }}>
        <div className="exam__dialog" style={{ boxShadow: 'none' }}>
          <p className="eyebrow">{moduleName}</p>
          <h2>{info?.title ?? 'Loading…'}</h2>

          <ul className="checklist">
            <li className="checklist__done">
              This paper lasts <strong>{formatDuration(info?.durationMs ?? null)}</strong>. The
              clock starts when you begin and cannot be paused.
            </li>
            {isListening && (
              <li className="checklist__done">
                The recording plays <strong>once</strong>. You cannot pause, rewind or replay it —
                only change the volume.
              </li>
            )}
            {info?.module === 'READING' && (
              <li className="checklist__done">
                You have <strong>60 minutes</strong> for all three passages. There is no extra time
                for transferring answers.
              </li>
            )}
            {isWritingPaper && (
              <li className="checklist__done">
                Two tasks in <strong>60 minutes</strong>. You choose how to divide the time — about
                20 minutes on Task 1 and 40 on Task 2 is the usual advice, and Task 2 counts double.
              </li>
            )}
            <li className="checklist__done">
              Your answers save as you type. If you lose connection, sign back in and continue.
            </li>
          </ul>

          {(error || infoError) && <p className="field__error">{error ?? infoError}</p>}

          {/*
            * The sound check, and — this is the part that was missing — the
            * reason the button below it will not respond until the check is
            * done. A candidate who cannot see the gate only sees a dead
            * button.
            */}
          {isListening && !info?.resuming && (
            <>
              {soundChecked ? (
                <p className="exam__gate exam__gate--done">
                  Sound checked. You can begin.
                </p>
              ) : (
                <>
                  <p className="exam__gate">
                    Check your sound before you begin — the recording plays once, and the clock
                    starts the moment you do.
                  </p>
                  <div className="exam__actions" style={{ justifyContent: 'flex-start' }}>
                    <button className="xbtn" onClick={() => void soundCheck()} disabled={checking}>
                      {checking ? 'Playing a test tone…' : 'Check my sound'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          <div className="exam__actions">
            <button className="xbtn" onClick={onLeave}>
              Not yet
            </button>
            <button
              className="xbtn xbtn--primary"
              disabled={starting || !info || (isListening && !soundChecked && !info.resuming)}
              title={
                !info
                  ? 'Still loading this paper'
                  : isListening && !soundChecked && !info.resuming
                    ? 'Check your sound first'
                    : undefined
              }
              onClick={() => {
                // Inside the click, so a strict browser lets the recording
                // start once the paper has loaded. See exam-audio.ts.
                if (isListening) unlockExamAudio();
                onStart();
              }}
            >
              {starting ? 'Opening…' : info?.resuming ? 'Resume the test' : 'Start the test'}
            </button>
          </div>
        </div>
      </div>
      <div />
    </div>
  );
}

type Outcome =
  | { status: 'RELEASED'; resultId: string }
  | { status: 'IN_PROGRESS' | 'MARKING' | 'RELEASE' | 'UNKNOWN'; resultId: null };

/** The finish screen's line about the result. Empty while it is being asked. */
function outcomeLine(outcome: Outcome | null, writing: boolean): string {
  if (!outcome) return '';
  switch (outcome.status) {
    case 'RELEASED':
      return 'Your result is ready.';
    case 'RELEASE':
      return 'Your teacher will release your result.';
    case 'MARKING':
      return writing
        ? 'Your teacher will mark your writing. Your result will appear in My results.'
        : 'Your result will appear in My results.';
    default:
      return 'Your result will appear in My results.';
  }
}

function Sit({ sittingId, onLeave }: { sittingId: string; onLeave: () => void }) {
  const {
    sitting,
    loadError,
    sebRequired,
    answers,
    setAnswer,
    msRemaining,
    submitted,
    answeredCount,
    recordingEnded,
  } = useSitting(sittingId);

  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  /**
   * Strict conditions, as far as a browser can hold them: the exam fills the
   * screen, and every time it leaves — a switched window, a hidden tab, full
   * screen ended — is recorded and counted against the sitting.
   */
  const lock = useLockdown(sittingId, !submitted);
  const [current, setCurrent] = useState(1);
  const [partIndex, setPartIndex] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [audioBlocked, setAudioBlocked] = useState(false);
  /** Why the recording would not play, when trying again on its own did not help. */
  const [audioFailed, setAudioFailed] = useState<string | null>(null);
  /**
   * Whether the recording has run out.
   *
   * The real exam announces the checking time inside the recording itself —
   * "you now have two minutes to check your answers". A centre's own audio is
   * whatever they uploaded, four files from a book more often than not, and
   * it usually says no such thing. Without this the recording simply stops
   * and nothing on screen tells the candidate the paper is still running.
   */
  const [recordingDone, setRecordingDone] = useState(false);
  // A candidate who reloads during the last 1m 50s lands back on the
  // countdown: the server remembers the recording stopped even if this page
  // was not there to hear it.
  useEffect(() => {
    if (sitting?.session.recordingEndedAt) setRecordingDone(true);
  }, [sitting?.session.recordingEndedAt]);
  const [recordingStarted, setRecordingStarted] = useState(false);
  /**
   * How many warnings the candidate has said they have read. A warning stands
   * until it is acknowledged, and the next breach raises the count past this
   * again, so the second warning is shown as plainly as the first.
   */
  const [warningSeen, setWarningSeen] = useState(0);
  const [split, setSplit] = useState(50);

  const audio = useRef<ListeningHandle>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const parts = sitting?.parts ?? [];
  const isListening = sitting?.test.module === 'LISTENING';
  const isWriting = sitting?.test.module === 'WRITING';
  // Reading and Writing both put source material beside the answer area, which
  // is how the real screen lays them out.
  const isSplit = sitting?.test.module === 'READING' || isWriting;

  const navParts = useMemo(
    () =>
      parts.map((p) => ({
        index: p.index,
        numbers: p.groups.flatMap((g) => g.questions.map((q) => q.number)),
      })),
    [parts],
  );

  const answeredSet = useMemo(
    () => new Set([...answers.entries()].filter(([, a]) => a.value.trim() !== '').map(([n]) => n)),
    [answers],
  );
  const flaggedSet = useMemo(
    () => new Set([...answers.entries()].filter(([, a]) => a.flagged).map(([n]) => n)),
    [answers],
  );

  // Start the recording as soon as the paper is on screen.
  useEffect(() => {
    if (!sitting || !isListening || submitted) return;
    void audio.current?.play();
  }, [sitting, isListening, submitted]);

  useEffect(() => {
    audio.current?.setVolume(volume);
  }, [volume]);

  /*
   * A handed-in paper takes its marking with it.
   *
   * Highlights live in this tab's storage so they survive moving between
   * parts and a reload mid-paper. Once the paper is in, they are of no use to
   * anyone — and on a shared machine in a centre, the next candidate must not
   * open their paper and find someone else's underlinings on it.
   */
  useEffect(() => {
    if (submitted) forgetMarks();
  }, [submitted]);

  /**
   * What the finish screen says about the result, asked once the paper is in.
   * A centre with instant results has already released a Listening or Reading
   * band by now, so the screen offers it instead of telling the candidate to
   * wait for their teacher. A paper closed by the clock may still be being
   * marked when the screen asks, so a machine-marked paper asks again briefly.
   */
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const writingPaper = sitting?.test.module === 'WRITING';
  useEffect(() => {
    if (!submitted) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ask = async (triesLeft: number) => {
      try {
        const { data } = await api.get<Outcome>(`/my/sittings/${sittingId}/outcome`);
        if (cancelled) return;
        if (data.status === 'MARKING' && !writingPaper && triesLeft > 0) {
          timer = setTimeout(() => void ask(triesLeft - 1), 1500);
          return;
        }
        setOutcome(data);
      } catch {
        if (!cancelled) setOutcome({ status: 'UNKNOWN', resultId: null });
      }
    };
    void ask(4);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [submitted, sittingId, writingPaper]);

  const goTo = useCallback(
    (n: number) => {
      setCurrent(n);
      setPartIndex(partOfQuestion(parts, n));
      requestAnimationFrame(() => {
        const el =
          document.getElementById(`q-${n}`) ??
          document.querySelector<HTMLElement>(`[aria-label="Question ${n}"]`);
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.focus();
      });
    },
    [parts],
  );

  // Dragging the divider between passage and questions, as the real Reading
  // screen allows.
  const onDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const host = bodyRef.current;
    if (!host) return;
    const move = (e: PointerEvent) => {
      const rect = host.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setSplit(Math.min(75, Math.max(25, pct)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  if (sebRequired) {
    return <NeedsSafeExamBrowser reason={sebRequired} onLeave={onLeave} />;
  }

  if (loadError) {
    return (
      <div className="exam" data-scheme="bw" data-size="regular">
        <div />
        <div className="exam__scroll">
          <div className="exam__dialog">
            <h2>This paper could not be opened</h2>
            <p>{loadError}</p>
            <div className="exam__actions">
              <button className="xbtn xbtn--primary" onClick={onLeave}>
                Back to my tests
              </button>
            </div>
          </div>
        </div>
        <div />
      </div>
    );
  }

  if (!sitting) {
    return (
      <div className="exam" data-scheme="bw" data-size="regular">
        <div />
        <div className="exam__scroll">
          <p>Opening the paper…</p>
        </div>
        <div />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="exam" data-scheme={settings.scheme} data-size={settings.size}>
        <div />
        <div className="exam__scroll" style={{ display: 'grid', placeItems: 'center' }}>
          <div className="exam__dialog xdone" style={{ boxShadow: 'none' }}>
            {/* The one moment of the sitting allowed to mark itself: the paper
                is in. A tick draws, in the screen's own black, once. */}
            <svg className="xdone__tick" viewBox="0 0 48 48" aria-hidden="true">
              <circle cx="24" cy="24" r="21" />
              <path d="M14.5 24.5 L21 31 L34 17.5" />
            </svg>
            <p className="eyebrow">Finished</p>
            <h2>{sitting.test.title}</h2>
            <p>
              Your paper has been submitted. You answered{' '}
              <strong>
                {answeredCount} of {navParts.flatMap((p) => p.numbers).length}
              </strong>{' '}
              questions.
            </p>
            {/* Held open while the screen asks, so the buttons do not jump. */}
            <p className="muted xdone__next">{outcomeLine(outcome, writingPaper)}</p>
            <div className="exam__actions">
              {outcome?.status === 'RELEASED' ? (
                <>
                  <button className="xbtn" onClick={onLeave}>
                    Back to my tests
                  </button>
                  <button
                    className="xbtn xbtn--primary"
                    onClick={() => navigate(`/my/results/${outcome.resultId}`)}
                  >
                    See your result
                  </button>
                </>
              ) : (
                <button className="xbtn xbtn--primary" onClick={onLeave}>
                  Back to my tests
                </button>
              )}
            </div>
          </div>
        </div>
        <div />
      </div>
    );
  }

  const part = parts.find((p) => p.index === partIndex) ?? parts[0];
  const partNumbers = navParts.find((p) => p.index === part?.index)?.numbers ?? [];

  /**
   * Matching headings is answered on the passage itself, in the other pane, so
   * the slots and the headings they take are worked out once here. A group
   * whose paragraphs the passage does not actually carry keeps its blanks
   * beside the questions instead of losing them altogether.
   */
  const passageSlots: PassageSlot[] = [];
  const headingBank = new Map<string, { letter: string; text: string }>();
  const inPassage = new Set<string>();
  for (const group of part?.groups ?? []) {
    const slots = headingSlots(group);
    if (!slots || !passageHasParagraphs(part?.passageHtml ?? null, slots)) continue;
    inPassage.add(group.id);
    for (const slot of slots) passageSlots.push({ ...slot, groupId: group.id });
    for (const option of group.optionsBank ?? []) headingBank.set(option.letter, option);
  }

  const questionsPane = (
    <Markable storageKey={`q-${part?.id ?? 'none'}`}>
      {/* For Writing the instructions sit beside the prompt, on the left. */}

      {part?.groups.map((group) => (
        <ExamGroup
          key={group.id}
          group={group}
          answers={answers}
          current={current}
          disabled={submitted}
          onChange={setAnswer}
          onFocusQuestion={setCurrent}
          headingsInPassage={inPassage.has(group.id)}
          module={sitting.test.module}
        />
      ))}
    </Markable>
  );

  return (
    <div
      className="exam"
      data-scheme={settings.scheme}
      data-size={settings.size}
      /* The module decides how wide an answer box needs to be: one word in
         Listening, up to three in Reading. */
      data-module={sitting.test.module}
    >
      <ExamTopBar
        candidateName={sitting.candidate.fullName}
        candidateNumber={sitting.candidate.candidateNumber}
        msRemaining={msRemaining}
        /*
         * A Listening paper shows no clock while the recording plays. The
         * recording is the pace, and the only number behind it until it stops
         * is a backstop minutes longer than the paper — showing that would be
         * telling a candidate they have time they do not. The clock appears
         * when the recording ends, reading 1m 50s, and is the whole of it: the
         * tail was briefly spelled out in a red-edged box on the left as well,
         * which said the same thing twice and sat over the instructions.
         */
        showClock={!isListening || recordingDone}
        settings={settings}
        onSettings={setSettings}
        volume={isListening ? volume : null}
        onVolume={setVolume}
        recording={
          // Only once sound is genuinely coming out. Saying "playing" while
          // the browser has blocked it would send a candidate looking for a
          // fault in their headphones instead of pressing the button.
          isListening && sitting.test.audio.length > 0 && !submitted && recordingStarted
            && !recordingDone && !audioFailed && !audioBlocked
            ? 'playing'
            : null
        }
        notes={[
          'Use the numbered buttons at the bottom to move between questions. Answered questions are shown in bold.',
          'The paper ends by itself when the time runs out. Your answers are saved as you make them.',
          'Select any text in the paper to highlight it or write a note on it. Press h to highlight.',
          ...(isListening
            ? ['The recording plays once. It cannot be paused, rewound or replayed.']
            : []),
        ]}
      />

      <div className="exam__body" ref={bodyRef}>
        {/*
          * The part header: the part on one line, what to do on the next,
          * inside a tinted box just in from the window's edges — which is how
          * the real screen sets it, wording included ("Read the text and
          * answer questions 1-13."). Where the paper states its own terms
          * they are shown instead.
          */}
        {part && (
          <div className="exam__taskbar">
            <p className="exam__taskbar__name">Part {part.index}</p>
            {part.instructions ? (
              <p className="exam__taskbar__terms">{part.instructions.replace(/\n/g, ' ')}</p>
            ) : (
              !isWriting &&
              partNumbers.length > 0 && (
                <p className="exam__taskbar__terms">
                  {isListening ? 'Listen and answer' : 'Read the text and answer'} questions{' '}
                  {partNumbers[0]}–{partNumbers[partNumbers.length - 1]}.
                </p>
              )
            )}
          </div>
        )}
        <DragProvider>
        {isSplit ? (
          <div
            className="exam__split"
            style={{ gridTemplateColumns: `${split}% 10px 1fr` }}
          >
            <div className="exam__pane exam__pane--left">
              {part?.passageTitle && <h2 className="paper__title">{part.passageTitle}</h2>}

              {/* Reading: the passage. Writing: the task and its artwork. */}
              {/*
                * A passage with a headings task is drawn with a drop slot above
                * each paragraph, by its own component — and that one was never
                * inside a Markable, so a candidate could not highlight a word of
                * it. Most Reading papers have a headings task in one passage.
                */}
              {part?.passageHtml &&
                (passageSlots.length > 0 ? (
                  <Markable storageKey={`p-${part.id}`}>
                    <PassagePane
                      html={part.passageHtml}
                      slots={passageSlots}
                      bank={headingBank}
                      answers={answers}
                      current={current}
                      disabled={submitted}
                      onChange={setAnswer}
                      onFocusQuestion={setCurrent}
                    />
                  </Markable>
                ) : (
                  <Markable storageKey={`p-${part.id}`}>
                    <div className="passage" data-markable dangerouslySetInnerHTML={{ __html: part.passageHtml }} />
                  </Markable>
                ))}

              {/*
                * A Writing task reads in the order the paper sets it: what the
                * chart shows and what to do with it first, in bold, and the
                * artwork under the words it describes. It was the other way
                * round here, which had a candidate meeting a chart before
                * being told what it was.
                */}
              {isWriting &&
                part?.groups.map((group) => (
                  <div key={group.id}>
                    {group.bodyHtml && (
                      <div
                        className="qbody xprompt"
                        dangerouslySetInnerHTML={{
                          __html: writingPromptHtml(
                            group.bodyHtml,
                            group.type === 'WRITING_TASK_2' ? 2 : 1,
                            sitting.test.variant === 'GENERAL' ? 'GENERAL' : 'ACADEMIC',
                          ),
                        }}
                      />
                    )}
                    {group.imageUrl && (
                      <figure className="qgroup__figure">
                        <img src={group.imageUrl} alt={`Figure for part ${group.fromQ}`} />
                      </figure>
                    )}
                  </div>
                ))}
            </div>
            <div
              className="exam__divider"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize the passage"
              tabIndex={0}
              onPointerDown={onDrag}
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') setSplit((s) => Math.max(25, s - 2));
                if (e.key === 'ArrowRight') setSplit((s) => Math.min(75, s + 2));
              }}
            />
            <div className="exam__pane">{questionsPane}</div>
          </div>
        ) : (
          <div className="exam__scroll">{questionsPane}</div>
        )}
        </DragProvider>
      </div>

      <ExamNav
        parts={navParts}
        currentPart={partIndex}
        current={current}
        answered={answeredSet}
        flagged={flaggedSet}
        /* Writing has no numbered questions to offer: its strip carries the
           two parts and nothing else, as the real screen's does. */
        numbered={!isWriting}
        onPart={(index) => {
          setPartIndex(index);
          const first = navParts.find((p) => p.index === index)?.numbers[0];
          if (first) setCurrent(first);
        }}
        onGo={goTo}
      />

      {isListening && sitting.test.audio.length > 0 && (
        <ListeningAudio
          ref={audio}
          tracks={sitting.test.audio}
          startOffsetMs={sitting.session.audioOffsetMs ?? 0}
          volume={volume}
          onBlocked={() => setAudioBlocked(true)}
          onFailed={(reason) => {
            setAudioFailed(reason);
            // Logged with the paper, so a teacher who hears about it can find it.
            console.error(`[listening] "${sitting.test.title}" would not play: ${reason}`);
          }}
          onPlaying={() => {
            setRecordingStarted(true);
            setAudioBlocked(false);
            setAudioFailed(null);
          }}
          onEnded={() => {
            setRecordingDone(true);
            void recordingEnded();
          }}
        />
      )}

      {/*
        * The screen is covered while the exam is not filling it. A browser
        * cannot refuse to leave full screen, so what it can do instead is give
        * a candidate nothing to read until they come back.
        */}
      {!lock.fullscreen && !lock.ended && !submitted && (
        <div className="exam__overlay exam__overlay--lock">
          <div className="exam__dialog">
            <h2>Return to your test</h2>
            <p>
              Your test must fill the screen. Leaving it is recorded as a breach of exam
              conditions.
            </p>
            <p className="muted">
              The clock is still running.{' '}
              {lock.breaches > 0 &&
                `You have had ${lock.breaches} of ${lock.limit} warnings.`}
            </p>
            <div className="exam__actions">
              <button className="xbtn xbtn--primary" onClick={lock.enterFullscreen}>
                Return to my test
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        * The warning a candidate gets for leaving the test, in the words an
        * invigilator would use: what they did, what it counts as, and what
        * happens next. It said "the exam left the screen" — which made the
        * exam the one who wandered off, and read like a machine describing its
        * own state rather than an examination telling a candidate where they
        * stand.
        */}
      {/*
        * A warning stays until the candidate says they have read it.
        *
        * It used to be shown only while the paper filled the screen, which is
        * the one moment it is certainly not being read: leaving full screen is
        * what the warning is about, so it vanished at the instant of the
        * breach and again whenever the browser dropped full screen on the way
        * back. A candidate reported seeing it for about three seconds — long
        * enough to notice something had appeared, not to read that the next
        * one ends their test.
        *
        * So it is tied to the breach rather than to the screen, and it takes
        * an acknowledgement. Dismissing it records the count that was read; a
        * further breach raises the count and the warning returns.
        */}
      {lock.breaches > warningSeen && !lock.ended && !submitted && (
        <BreachWarning
          count={lock.breaches}
          limit={lock.limit}
          onAcknowledge={() => setWarningSeen(lock.breaches)}
        />
      )}

      {lock.ended && (
        <div className="exam__overlay">
          <div className="exam__dialog">
            <h2>Your test has ended</h2>
            <p>
              {/* "twice" rather than "2 times", which is the count this almost
                  always is and the way a person would say it. */}
              You left your test {lock.breaches === 2 ? 'twice' : `${lock.breaches} times`}. Under
              exam conditions that ends the test.
            </p>
            <p>
              Your answers have been saved and your centre has been notified. Speak to your centre
              if you believe this is a mistake.
            </p>
            <div className="exam__actions">
              <button className="xbtn xbtn--primary" onClick={onLeave}>
                Back to my tests
              </button>
            </div>
          </div>
        </div>
      )}

      {/*
        * The recording itself failing — damaged, cut short, missing, or in a
        * format this browser cannot read — after the player has already
        * fetched it again on its own. Said as what it is: "your browser blocked
        * the sound" sent candidates pressing a button that could never help.
        */}
      {audioFailed && !audioBlocked && !submitted && (
        <div className="exam__overlay">
          <div className="exam__dialog">
            <h2>The recording has stopped</h2>
            <p>
              It could not be played on this computer — {audioFailed}. Your answers are saved.
            </p>
            <p>Tell your teacher, then press the button to try again.</p>
            <div className="exam__actions">
              <button className="xbtn" onClick={() => setAudioFailed(null)}>
                Close
              </button>
              <button
                className="xbtn xbtn--primary"
                onClick={() => {
                  setAudioFailed(null);
                  audio.current?.retry();
                }}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {audioBlocked && (
        <div className="exam__overlay">
          <div className="exam__dialog">
            <h2>Start the recording</h2>
            <p>
              Your browser blocked the sound. Press the button to begin — the recording is already
              playing on the clock, so do this now.
            </p>
            <div className="exam__actions">
              <button
                className="xbtn xbtn--primary"
                onClick={() => {
                  setAudioBlocked(false);
                  void audio.current?.play();
                }}
              >
                Play the recording
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
